/**
 * CONFIRM PAYMENT DIALOG
 * Dialog modal de confirmation manuelle virement/chèque
 * - Sélection du payeur (contact du dossier) obligatoire — TICKET-PAY-01
 * - Commentaire obligatoire tracé en timeline
 * - Email de confirmation au(x) participant(s) après confirmation d'un
 *   virement/chèque (TICKET-MAIL-06, symétrique du mail CB envoyé par
 *   l'IPN) — via Edge Function send-offline-confirmation. Non bloquant :
 *   un échec d'envoi n'annule pas la confirmation. La régularisation CB
 *   ancienne plateforme (PAY-12) n'envoie pas de mail.
 */

import { useEffect, useMemo, useState } from 'react';
import { Button } from '../ui/button';
import { Select } from '../ui/select';
import { Textarea } from '../ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { toast } from '../ui';
import { useSupabase } from '../../hooks/useSupabase';
import { usePaymentActions } from '../../hooks/usePaymentActions';
import { usePaymentCaseContacts } from '../../hooks/usePaymentCaseContacts';
import type { PayerCandidate } from '../../hooks/usePaymentCaseContacts';
import type { PaymentCaseWithRelations } from '../../hooks/usePaymentCase';
import {
  PAYMENT_METHOD,
  PAYMENT_SCOPE,
  PAYMENT_SCOPE_LABELS,
  PAYMENT_STATUS,
} from '../../types/payment';

interface ConfirmPaymentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  paymentCase: PaymentCaseWithRelations;
  prospectName: string;
  productName: string;
  onConfirmed: () => void;
}

function formatCandidateLabel(candidate: PayerCandidate): string {
  const fullName = `${candidate.first_name ?? ''} ${candidate.last_name ?? ''}`.trim() || 'Contact';
  if (candidate.role === 'pilot') return `${fullName} (pilote)`;
  if (candidate.role === 'copilot') return `${fullName} (copilote)`;
  return fullName;
}

// Langues supportées par les templates email côté 4LT (Resend + next-intl) —
// même liste que ResendBpiLinkDialog / BedLinkDialog.
const EMAIL_LOCALES = [
  { value: 'fr', label: 'Français' },
  { value: 'en', label: 'English' },
  { value: 'es', label: 'Español' },
] as const;

type EmailLocale = (typeof EMAIL_LOCALES)[number]['value'];

export function ConfirmPaymentDialog({
  open,
  onOpenChange,
  paymentCase,
  prospectName,
  productName,
  onConfirmed,
}: ConfirmPaymentDialogProps) {
  const [comment, setComment] = useState('');
  const [payerContactId, setPayerContactId] = useState<string>('');
  const [emailLocale, setEmailLocale] = useState<EmailLocale>('fr');
  const [submitting, setSubmitting] = useState(false);
  const supabase = useSupabase();
  const { confirmPayment } = usePaymentActions();

  const {
    candidates,
    loading: loadingCandidates,
    error: candidatesError,
  } = usePaymentCaseContacts({
    paymentCaseId: open ? paymentCase.id : undefined,
  });

  // Pré-sélection automatique :
  // - dossier solo (1 seul contact) → sélection immédiate, pas de blocage
  // - dossier avec payer_contact_id déjà défini (ex. cas IPN antérieur) → le sélectionner
  // - sinon → laisser l'utilisateur choisir
  useEffect(() => {
    if (!open) return;
    if (candidates.length === 0) return;

    if (payerContactId && candidates.some((c) => c.contact_id === payerContactId)) {
      return;
    }

    if (candidates.length === 1) {
      setPayerContactId(candidates[0].contact_id);
      return;
    }

    if (
      paymentCase.payer_contact_id &&
      candidates.some((c) => c.contact_id === paymentCase.payer_contact_id)
    ) {
      setPayerContactId(paymentCase.payer_contact_id);
    }
  }, [open, candidates, payerContactId, paymentCase.payer_contact_id]);

  const isManualCardConfirmation =
    paymentCase.status === PAYMENT_STATUS.PENDING_CARD &&
    paymentCase.method === PAYMENT_METHOD.CARD;

  const methodLabel = isManualCardConfirmation
    ? 'CB ancienne plateforme'
    : paymentCase.method === PAYMENT_METHOD.TRANSFER
      ? 'virement'
      : 'chèque';

  // BED : le montant confirmé est le RESTANT DÛ (des fractions CB ont pu être
  // encaissées avant un passage virement/chèque) — c'est ce que la RPC 00140
  // trace en payment_attempt. BPI : montant total du dossier.
  const isBed = paymentCase.scope === PAYMENT_SCOPE.BED;
  const succeededCents = isBed
    ? paymentCase.payment_attempts.reduce((acc, a) => (a.succeeded ? acc + a.amount_cents : acc), 0)
    : 0;
  const dueCents = isBed
    ? Math.max(0, paymentCase.amount_cents - succeededCents)
    : paymentCase.amount_cents;

  const amountFormatted = new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: paymentCase.currency || 'EUR',
  }).format(dueCents / 100);

  const payerOptions = useMemo(
    () =>
      candidates.map((candidate) => ({
        value: candidate.contact_id,
        label: formatCandidateLabel(candidate),
      })),
    [candidates]
  );

  // Verrouillage UI : le payeur n'est fige qu'apres un statut terminal
  // (paid, expired, failed). Avant, le staff peut corriger le payeur meme
  // s'il a deja ete identifie par le tunnel public. Aligne avec la
  // migration 00076 (payer_contact_id mutable until terminal).
  const TERMINAL_STATUSES = ['paid', 'expired', 'failed'] as const;
  const payerLocked =
    !!paymentCase.payer_contact_id &&
    candidates.some((c) => c.contact_id === paymentCase.payer_contact_id) &&
    TERMINAL_STATUSES.includes(paymentCase.status as (typeof TERMINAL_STATUSES)[number]);

  const canSubmit = !!comment.trim() && !!payerContactId && !submitting && !loadingCandidates;

  async function handleConfirm() {
    if (!comment.trim() || !payerContactId) return;

    setSubmitting(true);
    const result = await confirmPayment({
      paymentCaseId: paymentCase.id,
      comment: comment.trim(),
      contactId: payerContactId,
      payerContactId,
    });

    if (result.success) {
      if (isManualCardConfirmation) {
        toast.success('Paiement CB confirmé manuellement');
      } else {
        // Email de confirmation au(x) participant(s) — résolution serveur des
        // destinataires (Edge Function -> route interne 4LT, même règle que le
        // mail CB de l'IPN). Échec non bloquant : le paiement reste confirmé.
        try {
          const { error: invokeError } = await supabase.functions.invoke(
            'send-offline-confirmation',
            {
              body: { payment_case_id: paymentCase.id, locale: emailLocale },
            }
          );
          if (invokeError) throw invokeError;
          toast.success('Paiement confirmé — email de confirmation envoyé');
        } catch (err) {
          console.error('[ConfirmPaymentDialog] send-offline-confirmation error:', err);
          toast.error(
            "Paiement confirmé, mais l'envoi de l'email de confirmation a échoué. Vous pouvez prévenir le participant manuellement."
          );
        }
      }
      setComment('');
      setPayerContactId('');
      setEmailLocale('fr');
      onOpenChange(false);
      onConfirmed();
    } else {
      // Remonte le message specifique si fourni (ex. conflit de payeur),
      // sinon message generique.
      toast.error(result.error ?? 'Erreur lors de la confirmation du paiement');
    }
    setSubmitting(false);
  }

  function handleClose(value: boolean) {
    if (!submitting) {
      setComment('');
      setPayerContactId('');
      setEmailLocale('fr');
      onOpenChange(value);
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[460px]">
        <DialogHeader>
          <DialogTitle>
            {isManualCardConfirmation
              ? 'Confirmer paiement CB ancienne plateforme'
              : `Confirmer la réception du ${methodLabel}`}
          </DialogTitle>
          <DialogDescription>
            Prospect <strong>{prospectName}</strong> — {PAYMENT_SCOPE_LABELS[paymentCase.scope]}{' '}
            {productName} — <strong>{amountFormatted}</strong>
            {isBed ? ' (restant dû)' : ''}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          {isManualCardConfirmation && (
            <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
              Cette action confirme le paiement dans le CRM pour un encaissement CB réalisé hors
              module actuel. Elle ne crée pas de transaction Sogecommerce.
            </div>
          )}

          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-zinc-700">
              Payeur <span className="text-red-500">*</span>
            </label>
            {loadingCandidates ? (
              <p className="text-xs text-zinc-400">Chargement des contacts...</p>
            ) : candidatesError ? (
              <p className="text-xs text-red-500">{candidatesError}</p>
            ) : candidates.length === 0 ? (
              <p className="text-xs text-red-500">Aucun contact disponible pour ce dossier.</p>
            ) : (
              <Select
                value={payerContactId}
                onChange={(e) => setPayerContactId(e.target.value)}
                disabled={submitting || candidates.length === 1 || payerLocked}
                placeholder={candidates.length > 1 ? 'Sélectionner le payeur' : undefined}
                options={payerOptions}
                required
              />
            )}
            {payerLocked ? (
              <p className="text-xs text-zinc-500">
                Le payeur a été identifié lors du paiement en ligne et ne peut plus être modifié.
              </p>
            ) : (
              <p className="text-xs text-zinc-400">
                Le payeur sera tracé sur la transaction pour le suivi comptable.
              </p>
            )}
          </div>

          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-zinc-700">
              Commentaire <span className="text-red-500">*</span>
            </label>
            <Textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder={
                isManualCardConfirmation
                  ? 'Ex: Paiement CB confirmé sur ancienne plateforme le 28/03.'
                  : `Ex: ${methodLabel === 'virement' ? 'Virement reçu le 28/03, réf. bancaire VIR-...' : 'Chèque reçu le 28/03, n° ...'}`
              }
              rows={4}
              disabled={submitting}
            />
            <p className="text-xs text-zinc-400">
              Ce commentaire sera enregistré dans la timeline du dossier.
            </p>
          </div>

          {!isManualCardConfirmation && (
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-zinc-700">
                Langue de l&apos;email de confirmation
              </label>
              <Select
                value={emailLocale}
                onChange={(e) => setEmailLocale(e.target.value as EmailLocale)}
                disabled={submitting}
                options={EMAIL_LOCALES.map((l) => ({ value: l.value, label: l.label }))}
              />
              <p className="text-xs text-zinc-400">
                Un email de confirmation de paiement sera envoyé au(x) participant(s) du dossier.
              </p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleClose(false)} disabled={submitting}>
            Annuler
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={!canSubmit}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
          >
            {submitting ? 'Confirmation...' : 'Confirmer le paiement'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
